import { useContext, useEffect, useState } from 'react';
import { removeTokenAndReload } from '../../App';
import { ActivityExpiration } from '../../types/ActivityExpiration';
import { AppAgentContext } from '../../context/ContextProvider';
import { useTranslate } from 'sbx-crm-menu-ui';
import { appActions } from '../../store';
import { ModalType } from '../../types/Modal';
import { AlertTriangle } from 'lucide-react';
import { minutesToHours } from 'src/utils';

export const activityTimeout: ActivityExpiration = new ActivityExpiration({
    onFinished: (e) => {
        removeTokenAndReload();
        console.log('Session expired due to inactivity. Redirecting to login.');
        e.onDestroy();
    },
});

export const ValidationInactivityWrapper = ({ children }: { children: JSX.Element }) => {
    const {
        state: {
            session: { extra_setup },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const { t } = useTranslate('common');

    function alertTimeout() {
        const configs = extra_setup?.session?.activity_timeout;
        if (!configs || !Array.isArray(configs)) return;

        const aiStudio = configs.find((c) => c.section_app === 'ai-studio' && c.enable);
        const all = configs.find((c) => c.section_app === 'all' && c.enable);
        const sectionTime = aiStudio?.time ?? all?.time ?? 30;
        const time = minutesToHours(sectionTime);

        dispatch(
            appActions.openModal({
                type: ModalType.DYNAMIC,
                data: {
                    title: '',
                    className: '[&_button.absolute.top-4.right-4]:hidden',
                    children: () => {
                        const [timeLeft, setTimeLeft] = useState(60);
                        useEffect(() => {
                            if (timeLeft <= 0) {
                                activityTimeout.onFinished();
                                return;
                            }

                            const timer = setTimeout(() => {
                                setTimeLeft((prev) => {
                                    if (prev <= 1) {
                                        return 0;
                                    }
                                    return prev - 1;
                                });
                            }, 1000);

                            return () => clearTimeout(timer);
                        }, [timeLeft]);

                        return (
                            <div>
                                <div
                                    style={{
                                        margin: '0 auto 10px',
                                        height: 55,
                                        width: 55,
                                        background: '#fef3c7',
                                        borderRadius: '50%',
                                    }}
                                    className="flex items-center justify-center"
                                >
                                    <AlertTriangle style={{ height: 40, width: 40, color: '#d97706' }} />
                                </div>
                                <h2
                                    style={{ fontSize: 30, fontWeight: 600, textAlign: 'center' }}
                                    className="leading-[1.2] mb-5"
                                >
                                    {t('title_inactivity')}
                                </h2>
                                <p style={{ textAlign: 'center', marginBottom: 30 }}>
                                    {t('description_inactivity') + ` ${time}` + t('description_inactivity_2')}
                                </p>
                                <button
                                    disabled={timeLeft <= 0}
                                    style={{
                                        width: '250px',
                                        margin: '0 auto 5px',
                                        display: 'block',
                                    }}
                                    className="btn btn-primary"
                                    onClick={() => {
                                        activityTimeout.resetInactivityTimer();
                                        dispatch(appActions.closeModal(ModalType.DYNAMIC));
                                    }}
                                >
                                    {t('continue')} {'('}
                                    {timeLeft} seg{')'}
                                </button>
                            </div>
                        );
                    },
                },
            })
        );
    }

    useEffect(() => {
        const configs = extra_setup?.session?.activity_timeout;
        if (!configs || !Array.isArray(configs)) return;

        // find first "ai-studio"
        const aiStudio = configs.find((c) => c.section_app === 'ai-studio' && c.enable);
        // if ai-studio doesn´t exist, we find "all"
        const all = configs.find((c) => c.section_app === 'all' && c.enable);

        const sectionTime = aiStudio?.time ?? all?.time;

        if (sectionTime) {
            activityTimeout.setTimeout(sectionTime);
        }

        if (aiStudio?.enable || all?.enable) {
            activityTimeout.setOnInactivity(alertTimeout);
            activityTimeout.onStart();
        }
    }, [extra_setup]);

    return <>{children}</>;
};
